import { Space } from 'component/Space/Space'
import { Typo } from 'component/Typo/Typo'
import { Vertical } from 'component/Vertical/Vertical'
import { i18n } from 'i18n'
import { observer } from 'mobx-react-lite'
import { Filter } from 'page/catalog/DemoCatalogFilters/Filter'
import { useDemo } from 'page/catalog/store/CatalogContext'
import { comboCartStore } from 'page/catalog/store/ComboCartStore'
import { useEffect } from 'react'
import { ui } from 'store/home/ui/HomeUiStore'
import { Item } from 'type/Item'
import css from './ComboCartColumn.module.scss'
import { ComboCartColumnProps } from './ComboCartColumn.types'
import { ComboCartItem } from './ComboCartItem'
import { SearchInput } from './SearchInput'

export const ComboCartColumn = observer(
  ({
     title,
     items,
     selectedItemId,
     onSelectItem,
     onRemoveItem,
     emptyMessage,
     options,
     product,
     showFilters = true,
     showResetButton = false,
     onFiltersCountChange,
     filterType = 'top',
   }: ComboCartColumnProps) => {
    const filters =
      filterType === 'top'
        ? comboCartStore.topFilters
        : comboCartStore.bottomFilters
    const { brands, colors, search } = filters

    const handleSearchChange = (value: string) => {
      if (filterType === 'top') {
        comboCartStore.setTopSearch(value)
      } else {
        comboCartStore.setBottomSearch(value)
      }
    }

    const brandOptions = options?.brands
    const colorOptions = options?.colors
    const showBrandFilter = product?.brandFilter
    const showColorFilter = product?.colorFilter

    const { mobile } = ui.window
    const { stand } = useDemo()

    const resetAllFilters = () => {
      if (filterType === 'top') {
        comboCartStore.clearTopFilters()
      } else {
        comboCartStore.clearBottomFilters()
      }
    }

    const hasActiveFilters =
      brands.value.length > 0 ||
      colors.value.length > 0

    const totalActiveFilters =
      brands.value.length + colors.value.length

    useEffect(() => {
      if (onFiltersCountChange) {
        onFiltersCountChange(totalActiveFilters)
      }
    }, [totalActiveFilters, onFiltersCountChange])

    const filterItems = (items: Item[]) => {
      return items.filter((item) => {
        if (
          search &&
          !item.product_name?.toLowerCase().includes(search.toLowerCase())
        ) {
          return false
        }

        if (brands.value.length > 0) {
          if (!brands.value.includes(item.brand_name || '')) {
            return false
          }
        }

        if (colors.value.length > 0) {
          const itemColor = item.dress_colors || ''
          const colorMatch = colors.value.some((color) => {
            // Нормализуем буквы "е" и "ё" для корректного сравнения
            const normalizedItemColor = itemColor
              .toLowerCase()
              .replace(/ё/g, 'е')
            const normalizedColor = color.toLowerCase().replace(/ё/g, 'е')
            return normalizedColor === normalizedItemColor
          })

          if (!colorMatch) {
            return false
          }
        }

        return true
      })
    }

    const filteredItems = filterItems(items)

    return (
      <Vertical className={css.column}>
        {!mobile && (
          <Space className={css.columnHeader}>
            <Typo size={13} upper>
              {title} <Typo secondary>{filteredItems.length}</Typo>
            </Typo>
          </Space>
        )}

        {showFilters && (
          <Vertical className={css.filters} gap={12}>
            <Space className={css.filtersRow} gap={8} wrap>
              {showBrandFilter && brandOptions && (
                <Filter filter={brands} options={brandOptions} buttonPadSmall />
              )}

              {showColorFilter && colorOptions && (
                <Filter filter={colors} options={colorOptions} buttonPadSmall />
              )}

              {showResetButton && hasActiveFilters && (
                <button
                  className={css.resetButton}
                  onClick={resetAllFilters}
                  type="button"
                >
                  {i18n('combo.Reset')}
                </button>
              )}
            </Space>

            {!stand && <SearchInput
              value={search}
              onChange={handleSearchChange}
              placeholder={i18n('Search')}
            />}
          </Vertical>
        )}

        <div className={css.columnItemsContainer}>
          <div className={css.topGradient} />
          <div className={css.columnItems}>
            {filteredItems.map((item) => (
              <ComboCartItem
                key={item.item_id}
                item={item}
                isSelected={selectedItemId === item.item_id}
                onSelect={() => onSelectItem(item.item_id)}
                onRemove={() => onRemoveItem(item.item_id)}
              />
            ))}
            {filteredItems.length === 0 && (
              <Space className={css.emptyColumn} center>
                <Typo size={12} secondary>
                  {emptyMessage}
                </Typo>
              </Space>
            )}
          </div>
          <div className={css.bottomGradient} />
        </div>
      </Vertical>
    )
  },
)
